import React, { useState, useMemo } from "react";
import { useAuthQuery as useQuery } from '../../shared/hooks/useAuthQuery';
import { api } from "../../convex/_generated/api";
import { Button } from "../../catalyst-components/button";
import { Input } from "../../catalyst-components/input";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "../../catalyst-components/table";
import { Badge } from "../../catalyst-components/badge";
import { useNavigate } from "react-router-dom";
import { MainRoutes } from "../../shared/models";
import { ArrowLeftIcon, UserGroupIcon, MagnifyingGlassIcon, EyeIcon } from "@heroicons/react/24/outline";
import { formatDuration } from "../../shared/utils/formatDuration";

export const UsersPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const usersPerPage = 20;

    const users = useQuery(api.users.getAllUsers);
    const onlineUsers = useQuery(api.presence.getOnlineUsersWithDetails);
    const screenTimeSummaries = useQuery(api.analytics.getUserScreenTimeSummaries);

    // Create a map of online user IDs for quick lookup
    const onlineUserIds = useMemo(() => {
        if (!onlineUsers) return new Set<string>();
        return new Set(onlineUsers.map(u => u.userId));
    }, [onlineUsers]);

    const filteredUsers = useMemo(() => {
        if (!users) return [];

        const filtered = users.filter(user =>
            user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (user.firstName && user.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (user.lastName && user.lastName.toLowerCase().includes(searchTerm.toLowerCase()))
        );

        // Sort: online users first, then by best-known last activity descending.
        // Falls back to presence lastSeen, then account creation time if lastLoginDate is missing.
        return filtered.sort((a, b) => {
            const aOnline = onlineUserIds.has(a._id);
            const bOnline = onlineUserIds.has(b._id);
            if (aOnline !== bOnline) return aOnline ? -1 : 1;
            const aDate = a.lastLoginDate ? new Date(a.lastLoginDate).getTime() : (a.lastPresence ?? a._creationTime ?? 0);
            const bDate = b.lastLoginDate ? new Date(b.lastLoginDate).getTime() : (b.lastPresence ?? b._creationTime ?? 0);
            return bDate - aDate;
        });
    }, [users, searchTerm, onlineUserIds]);

    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
    const startIndex = (currentPage - 1) * usersPerPage;
    const endIndex = startIndex + usersPerPage;
    const currentUsers = filteredUsers.slice(startIndex, endIndex);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    if (!users) {
        return (
            <div className="min-h-screen bg-background pt-24">
                <div className="border-b border-border/50 bg-gradient-to-r from-green-50 to-green-100/50 px-6 py-8">
                    <div>
                        <h1 className="text-3xl font-semibold text-foreground">Users Management</h1>
                    </div>
                </div>
                <div className="flex justify-center items-center h-64">
                    <div className="text-lg text-muted-foreground">Loading users...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background pt-24">
            {/* Header */}
            <div className="border-b border-border/50 bg-gradient-to-r from-green-50 to-green-100/50 px-6 py-8">
                <div>
                    <div className="flex items-center gap-4 mb-4">
                        <Button
                            color="secondary"
                            className="rounded-full"
                            onClick={() => navigate(MainRoutes.Admin)}
                        >
                            <ArrowLeftIcon className="h-4 w-4 mr-2" />
                            Back
                        </Button>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-xl bg-white border-2 border-green-200">
                            <UserGroupIcon className="h-8 w-8 text-primary" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-semibold text-foreground mb-1">Users Management</h1>
                            <p className="text-base text-muted-foreground">
                                View and manage all platform users
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="px-6 py-8">
                <div className="space-y-6">
                    {/* Search and Stats */}
                    <div className="border-2 border-border/50 bg-card p-6 rounded-xl">
                        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                            <div className="flex-1 max-w-md relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                                <Input
                                    type="text"
                                    placeholder="Search users by email, first name, or last name..."
                                    value={searchTerm}
                                    className="pl-10"
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="px-4 py-2 bg-muted/30 border border-border/50 rounded-full">
                                    <span className="text-sm text-muted-foreground">Total: </span>
                                    <span className="font-semibold text-foreground">{filteredUsers.length}</span>
                                    <span className="text-sm text-muted-foreground"> of </span>
                                    <span className="font-semibold text-foreground">{users.length}</span>
                                </div>
                                {onlineUsers && (
                                    <div className="px-4 py-2 bg-green-50 border-2 border-green-200 rounded-full">
                                        <span className="text-sm font-medium text-green-700">
                                            {onlineUsers.length} online
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Users Table */}
                    <div className="border-2 border-border/50 rounded-xl overflow-hidden bg-card">
                        <Table>
                            <TableHead>
                                <TableRow className="bg-muted/30">
                                    <TableHeader>User ID</TableHeader>
                                    <TableHeader>Email</TableHeader>
                                    <TableHeader>First Name</TableHeader>
                                    <TableHeader>Last Name</TableHeader>
                                    <TableHeader>Status</TableHeader>
                                    <TableHeader>Last Seen</TableHeader>
                                    <TableHeader>Total Time</TableHeader>
                                    <TableHeader>Active</TableHeader>
                                    <TableHeader>Idle</TableHeader>
                                    <TableHeader>Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {currentUsers.map((user) => (
                                    <TableRow
                                        key={user._id}
                                        className="hover:bg-muted/20 cursor-pointer"
                                        onClick={() => navigate(`/admin/users/${user._id}`)}
                                    >
                                        <TableCell className="font-mono text-sm text-muted-foreground">
                                            {user._id.slice(0, 12)}...
                                        </TableCell>
                                        <TableCell className="font-medium text-foreground">
                                            {user.email}
                                        </TableCell>
                                        <TableCell>
                                            {user.firstName || (
                                                <span className="text-muted-foreground italic">Not provided</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {user.lastName || (
                                                <span className="text-muted-foreground italic">Not provided</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {onlineUserIds.has(user._id) ? (
                                                <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-full">
                                                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                                                    Online
                                                </span>
                                            ) : (
                                                <span className="text-muted-foreground">Offline</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {onlineUserIds.has(user._id)
                                                ? 'Now'
                                                : user.lastLoginDate
                                                    ? new Date(user.lastLoginDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                                    : user.lastPresence
                                                        ? new Date(user.lastPresence).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                                        : 'Never'}
                                        </TableCell>
                                        <TableCell className="text-sm font-mono text-muted-foreground">
                                            {formatDuration(screenTimeSummaries?.[user._id]?.total)}
                                        </TableCell>
                                        <TableCell className="text-sm font-mono text-green-600">
                                            {formatDuration(screenTimeSummaries?.[user._id]?.active)}
                                        </TableCell>
                                        <TableCell className="text-sm font-mono text-orange-500">
                                            {formatDuration(screenTimeSummaries?.[user._id]?.idle)}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                color="secondary"
                                                className="rounded-full"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/admin/users/${user._id}`);
                                                }}
                                            >
                                                <EyeIcon className="h-4 w-4 mr-1" />
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="px-6 py-4 border-t border-border/50 bg-muted/20">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm text-muted-foreground">
                                        Showing {startIndex + 1} to {Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} results
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            color="secondary"
                                            className="rounded-full"
                                            disabled={currentPage === 1}
                                            onClick={() => handlePageChange(currentPage - 1)}
                                        >
                                            Previous
                                        </Button>

                                        {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                            // Show pages around current page
                                            let page;
                                            if (totalPages <= 5) {
                                                page = i + 1;
                                            } else if (currentPage <= 3) {
                                                page = i + 1;
                                            } else if (currentPage >= totalPages - 2) {
                                                page = totalPages - 4 + i;
                                            } else {
                                                page = currentPage - 2 + i;
                                            }
                                            return (
                                                <Button
                                                    key={page}
                                                    color={currentPage === page ? "primary" : "secondary"}
                                                    className="rounded-full min-w-10"
                                                    onClick={() => handlePageChange(page)}
                                                >
                                                    {page}
                                                </Button>
                                            );
                                        })}

                                        <Button
                                            color="secondary"
                                            className="rounded-full"
                                            disabled={currentPage === totalPages}
                                            onClick={() => handlePageChange(currentPage + 1)}
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* No Results */}
                    {filteredUsers.length === 0 && searchTerm && (
                        <div className="text-center py-12 bg-muted/30 rounded-xl">
                            <div className="text-muted-foreground text-lg mb-4">
                                No users found matching "{searchTerm}"
                            </div>
                            <Button
                                color="secondary"
                                className="rounded-full"
                                onClick={() => setSearchTerm("")}
                            >
                                Clear Search
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
